import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const EMPTY_FORM = {
  name: '', price: '', credits: '1', duration_days: '30',
  features: '', domain_url: '', is_active: '1',
};

export default function AdminPRPackages() {
  return <PackageManager type="press_release" typeLabel="Press Release" />;
}

export function PackageManager({ type, typeLabel }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const { getIdToken }          = useAuth();

  useEffect(() => { fetchPackages(); }, []);

  function fetchPackages() {
    axios.get(`${API_URL}/api/packages.json?type=${type}`)
      .then((res) => { setPackages(res.data.packages || []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  function openAdd() {
    setForm({ ...EMPTY_FORM }); setEditingId(null); setError(''); setSuccess(''); setShowForm(true);
  }

  function openEdit(pkg) {
    setForm({
      name:         pkg.name,
      price:        String(pkg.price),
      credits:      String(pkg.credits),
      duration_days: String(pkg.durationDays),
      features:     Array.isArray(pkg.features) ? pkg.features.join('\n') : '',
      domain_url:   pkg.domainUrl || '',
      is_active:    pkg.isActive ? '1' : '0',
    });
    setEditingId(pkg.id); setError(''); setSuccess(''); setShowForm(true);
  }

  function cancel() {
    setShowForm(false); setEditingId(null); setForm({ ...EMPTY_FORM }); setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setSuccess(''); setSaving(true);
    const token = getIdToken();
    const payload = {
      name:         form.name.trim(),
      type,
      price:        parseFloat(form.price) || 0,
      credits:      parseInt(form.credits) || 1,
      duration_days: parseInt(form.duration_days) || 30,
      features:     JSON.stringify(form.features.split('\n').map((f) => f.trim()).filter(Boolean)),
      domain_url:   form.domain_url.trim() || null,
      is_active:    form.is_active === '1' ? 1 : 0,
    };

    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/packages/edit.json?id=${editingId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setSuccess('Package updated.');
      } else {
        await axios.post(`${API_URL}/api/packages.json`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setSuccess('Package created.');
      }
      setShowForm(false); setEditingId(null); setForm({ ...EMPTY_FORM }); fetchPackages();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save package.');
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this package?')) return;
    const token = getIdToken();
    try {
      await axios.delete(`${API_URL}/api/packages/remove.json?id=${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setPackages((prev) => prev.filter((p) => p.id !== id));
    } catch {
      setError('Failed to delete package.');
    }
  }

  return (
    <div>
      <div style={s.topBar}>
        <h2 style={s.pageTitle}>{typeLabel} Packages</h2>
        {!showForm && <button onClick={openAdd} style={s.addBtn}>+ Add Package</button>}
      </div>

      {success && <div style={s.success}>{success}</div>}
      {error   && <div style={s.error}>{error}</div>}

      {showForm && (
        <div style={s.formCard}>
          <h3 style={s.formTitle}>{editingId ? 'Edit Package' : 'New Package'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={s.twoCol}>
              <Field label="Package Name *">
                <input style={s.input} type="text" value={form.name} onChange={set('name')} placeholder="e.g. Basic PR Package" required />
              </Field>
              <Field label="Price ($) *">
                <input style={s.input} type="number" min="0" step="0.01" value={form.price} onChange={set('price')} placeholder="29.99" required />
              </Field>
            </div>
            <div style={s.threeCol}>
              <Field label="Credits">
                <input style={s.input} type="number" min="1" value={form.credits} onChange={set('credits')} />
              </Field>
              <Field label="Duration (days)">
                <input style={s.input} type="number" min="1" value={form.duration_days} onChange={set('duration_days')} />
              </Field>
              <Field label="Active">
                <select style={s.input} value={form.is_active} onChange={set('is_active')}>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </Field>
            </div>
            <Field label="Domain / Site URL">
              <input style={s.input} type="url" value={form.domain_url} onChange={set('domain_url')} placeholder="https://example.com" />
            </Field>
            <Field label="Features (one per line)">
              <textarea style={{ ...s.input, resize: 'vertical' }} rows={4} value={form.features} onChange={set('features')} placeholder={"Do-Follow Links\nUp to 500 words\nDistribution to 100+ outlets"} />
            </Field>
            <div style={s.formActions}>
              <button type="button" onClick={cancel} style={s.btnOutline}>Cancel</button>
              <button type="submit" disabled={saving} style={s.btnPrimary}>
                {saving ? 'Saving...' : editingId ? 'Update Package' : 'Create Package'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div style={s.loading}>Loading...</div>
      ) : packages.length === 0 && !showForm ? (
        <div style={s.empty}>No packages yet. Add your first one.</div>
      ) : (
        <div style={s.card}>
          <div style={s.tableHead}>
            <span>Name</span><span>Price</span><span>Credits</span><span>Duration</span><span>Domain</span><span>Active</span><span></span>
          </div>
          {packages.map((pkg) => (
            <div key={pkg.id} style={s.tableRow}>
              <span style={s.pkgName}>{pkg.name}</span>
              <span style={s.cell}>${pkg.price.toFixed(2)}</span>
              <span style={s.cell}>{pkg.credits}</span>
              <span style={s.cell}>{pkg.durationDays}d</span>
              <span style={s.cellSmall}>{pkg.domainUrl ? pkg.domainUrl.replace(/^https?:\/\//, '') : '—'}</span>
              <span style={{ ...s.badge, ...(pkg.isActive ? s.badgeActive : s.badgeInactive) }}>
                {pkg.isActive ? 'Active' : 'Inactive'}
              </span>
              <span style={s.actions}>
                <button onClick={() => openEdit(pkg)} style={s.editBtn}>Edit</button>
                <button onClick={() => handleDelete(pkg.id)} style={s.deleteBtn}>Delete</button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#444', marginBottom: '0.3rem' }}>{label}</label>
      {children}
    </div>
  );
}

const s = {
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' },
  pageTitle: { margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#1a1a2e' },
  addBtn: { padding: '0.5rem 1.1rem', background: '#e94560', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' },
  success: { background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem', color: '#15803d', fontSize: '0.9rem' },
  error: { background: '#fee', border: '1px solid #fcc', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem', color: '#c00', fontSize: '0.9rem' },
  loading: { textAlign: 'center', padding: '3rem', color: '#888' },
  empty: { textAlign: 'center', padding: '3rem', color: '#888', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  formCard: { background: '#fff', borderRadius: 10, padding: '2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: '2rem', border: '2px solid #e94560' },
  formTitle: { margin: '0 0 1.5rem', fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  threeCol: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' },
  input: { width: '100%', padding: '0.6rem 0.9rem', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'system-ui, sans-serif' },
  formActions: { display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '0.5rem' },
  btnPrimary: { padding: '0.65rem 1.4rem', background: '#e94560', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
  btnOutline: { padding: '0.65rem 1.4rem', background: 'transparent', color: '#555', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
  card: { background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' },
  tableHead: { display: 'grid', gridTemplateColumns: '1.5fr 80px 80px 80px 1fr 80px 130px', padding: '0.7rem 1.25rem', background: '#f8f8f8', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#999', borderBottom: '1px solid #eee' },
  tableRow: { display: 'grid', gridTemplateColumns: '1.5fr 80px 80px 80px 1fr 80px 130px', padding: '0.9rem 1.25rem', borderBottom: '1px solid #f0f0f0', alignItems: 'center' },
  pkgName: { fontWeight: 600, fontSize: '0.875rem', color: '#1a1a2e' },
  cell: { fontSize: '0.875rem', color: '#555' },
  cellSmall: { fontSize: '0.78rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badge: { fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.55rem', borderRadius: 20, display: 'inline-block' },
  badgeActive: { background: '#dcfce7', color: '#15803d' },
  badgeInactive: { background: '#f3f4f6', color: '#6b7280' },
  actions: { display: 'flex', gap: '0.5rem' },
  editBtn: { padding: '0.25rem 0.65rem', background: '#f0f0f0', color: '#444', border: 'none', borderRadius: 4, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 },
  deleteBtn: { padding: '0.25rem 0.65rem', background: '#fff0f0', color: '#dc2626', border: 'none', borderRadius: 4, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 },
};
